import Phaser from "phaser";
import { GameEvents } from "../core/Events";
import { SPELLS, type SpellId } from "../combat/SpellDefinitions";

const UI = 100_000;
const GOLD = 0xc99a42;
const PALE = "#ead9ae";
const MUTED = "#9d8c6b";

type Slot = { shade: Phaser.GameObjects.Rectangle; timer: Phaser.GameObjects.Text; icon: Phaser.GameObjects.Graphics };

export class Hud {
  private readonly hpFill: Phaser.GameObjects.Graphics;
  private readonly manaFill: Phaser.GameObjects.Graphics;
  private readonly bossFill: Phaser.GameObjects.Rectangle;
  private readonly bossText: Phaser.GameObjects.Text;
  private readonly killText: Phaser.GameObjects.Text;
  private readonly slots = new Map<SpellId, Slot>();
  private hp = 1050; private maxHp = 1050; private mana = 850; private maxMana = 850; private kills = 0;

  constructor(private readonly scene: Phaser.Scene, private readonly totalEnemies: number) {
    this.createFrame();
    this.hpFill = scene.add.graphics().setScrollFactor(0).setDepth(UI+5);
    this.manaFill = scene.add.graphics().setScrollFactor(0).setDepth(UI+5);
    this.drawOrb(this.hpFill, 380, 983, 80, 0xa30912, 1);
    this.drawOrb(this.manaFill, 1540, 983, 80, 0x0657a9, 1);

    this.bossFill = scene.add.rectangle(960, 58, 710, 13, 0x9d1726, 1).setOrigin(0.5).setScrollFactor(0).setDepth(UI+4);
    this.bossText = scene.add.text(960, 18, "KRÓL SZKIELETÓW  •  FAZA 1", this.titleStyle(22)).setOrigin(0.5,0).setScrollFactor(0).setDepth(UI+5);
    this.killText = scene.add.text(1662, 382, `Szkielety: 0 / ${totalEnemies}`, this.bodyStyle(15)).setScrollFactor(0).setDepth(UI+5);

    this.createSpellBar();
    this.createSidePanels();
    this.redrawOrbs();

    scene.events.on(GameEvents.PlayerHealthChanged, this.onHealthChanged, this);
    scene.events.on(GameEvents.PlayerManaChanged, this.onManaChanged, this);
    scene.events.on(GameEvents.BossHealthChanged, this.onBossHealthChanged, this);
    scene.events.on(GameEvents.SpellCooldownChanged, this.onSpellCooldownChanged, this);
    scene.events.on(GameEvents.EnemyDefeated, this.onEnemyDefeated, this);
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, this.destroy, this);
  }

  private createFrame(): void {
    const g = this.scene.add.graphics().setScrollFactor(0).setDepth(UI);
    g.fillStyle(0x030303,0.86); g.fillRect(0,0,1920,94); g.fillRect(0,820,1920,260); g.fillRect(0,94,300,726); g.fillRect(1605,94,315,726);
    g.lineStyle(2,GOLD,0.78); g.lineBetween(0,94,1920,94); g.lineBetween(300,94,300,820); g.lineBetween(1605,94,1605,820); g.lineBetween(0,820,1920,820);
    g.lineStyle(1,0x6f481d,0.55);
    for(let x=0;x<1920;x+=42){ g.lineBetween(x,820,x+24,846); }

    this.scene.add.text(30,18,"MAG  •  POZIOM 15",this.titleStyle(20)).setScrollFactor(0).setDepth(UI+4);
    this.scene.add.text(34,50,"ŻYCIE 1050 / 1050",this.bodyStyle(14)).setScrollFactor(0).setDepth(UI+4);
    this.scene.add.text(34,70,"MANA 850 / 850",this.bodyStyle(14)).setScrollFactor(0).setDepth(UI+4);
    this.scene.add.text(1642,112,"MINIMAPA",this.titleStyle(18)).setScrollFactor(0).setDepth(UI+4);
    const map = this.scene.add.graphics().setScrollFactor(0).setDepth(UI+3);
    map.fillStyle(0x050505,0.96); map.fillCircle(1762,225,112); map.lineStyle(3,GOLD,0.75); map.strokeCircle(1762,225,112);
    map.lineStyle(3,0x4e4538,0.7);
    for(let i=0;i<14;i+=1){ const a=(Math.PI*2*i)/14; const r=Phaser.Math.Between(18,88); map.lineBetween(1762+Math.cos(a)*12,225+Math.sin(a)*12,1762+Math.cos(a)*r,225+Math.sin(a)*r); }
    map.fillStyle(0xd4aa48,1); map.fillTriangle(1762,210,1755,228,1769,228);
  }

  private createSidePanels(): void {
    this.panel(18,130,264,132,"ZASOBY");
    this.scene.add.text(38,174,"Złoto     2 455\nKryształy    10\nKlucze         0",this.bodyStyle(17)).setLineSpacing(14).setScrollFactor(0).setDepth(UI+5);
    this.panel(18,290,264,328,"EFEKTY AKTYWNE");
    ["Płomienna Potęga","Sowie Zauroczenie","Więzy Zguby","Demoniczny Płaszcz","Sobota Bonusowa"].forEach((t,i)=>{
      const y=340+i*54; const c=[0xff5a18,0x2478ff,0x57c44d,0xb84dff,0x4a94ff][i]!;
      this.scene.add.circle(46,y,17,c,0.9).setStrokeStyle(2,0xe6c27b,0.6).setScrollFactor(0).setDepth(UI+5);
      this.scene.add.text(74,y-10,t,this.bodyStyle(14)).setScrollFactor(0).setDepth(UI+5);
    });
    this.panel(1620,350,282,126,"CEL");
    this.scene.add.text(1642,422,"Pokonaj Króla Szkieletów",this.bodyStyle(15)).setScrollFactor(0).setDepth(UI+5);
    this.panel(1620,500,282,250,"RANKING CZASU");
    this.scene.add.text(1642,548,"1. Arkanis       07:32\n2. Wega          08:18\n3. Niezłomny     09:47\n4. Serena        09:21\n5. Czarodziej    13:33",this.bodyStyle(15)).setLineSpacing(16).setScrollFactor(0).setDepth(UI+5);
  }

  private panel(x:number,y:number,w:number,h:number,title:string):void{
    const g=this.scene.add.graphics().setScrollFactor(0).setDepth(UI+1); g.fillStyle(0x050403,0.9); g.fillRect(x,y,w,h); g.lineStyle(2,GOLD,0.68); g.strokeRect(x,y,w,h); g.lineStyle(1,0x5d3a16,0.7); g.lineBetween(x+10,y+38,x+w-10,y+38);
    this.scene.add.text(x+16,y+10,title,this.titleStyle(17)).setScrollFactor(0).setDepth(UI+5);
  }

  private createSpellBar(): void {
    const startX=540, y=858, size=100;
    SPELLS.forEach((spell,i)=>{
      const x=startX+i*112;
      const g=this.scene.add.graphics().setScrollFactor(0).setDepth(UI+3);
      g.fillStyle(0x070606,0.96); g.fillRect(x,y,size,size); g.lineStyle(2,GOLD,0.72); g.strokeRect(x,y,size,size);
      const colors=[0xff5b18,0x31a7ff,0x8a39ef,0x5f70ff,0xe8b83d,0xb22cff]; const c=colors[i]!;
      g.fillStyle(c,0.3); g.fillCircle(x+50,y+45,31); g.lineStyle(5,c,0.95);
      for(let n=0;n<5;n+=1){ const a=(Math.PI*2*n)/5+i; g.lineBetween(x+50,y+45,x+50+Math.cos(a)*28,y+45+Math.sin(a)*28); }
      this.scene.add.text(x+7,y+5,String(spell.key),{fontFamily:"Georgia",fontSize:"15px",color:"#f4d891"}).setScrollFactor(0).setDepth(UI+5);
      this.scene.add.text(x+50,y+78,spell.label,{fontFamily:"Georgia",fontSize:"12px",color:PALE,align:"center",wordWrap:{width:92}}).setOrigin(0.5,0).setScrollFactor(0).setDepth(UI+5);
      const shade=this.scene.add.rectangle(x,y,size,size,0x000000,0.76).setOrigin(0).setScrollFactor(0).setDepth(UI+7).setVisible(false);
      const timer=this.scene.add.text(x+50,y+45,"",{fontFamily:"Georgia",fontSize:"24px",color:"#ffffff"}).setOrigin(0.5).setScrollFactor(0).setDepth(UI+8).setVisible(false);
      this.slots.set(spell.id,{shade,timer,icon:g});
    });
    this.scene.add.text(940,1022,"Krzysztof Różycki (c) 2026",{fontFamily:"Georgia",fontSize:"13px",color:"#765b31"}).setOrigin(0.5).setScrollFactor(0).setDepth(UI+4);
  }

  private drawOrb(g:Phaser.GameObjects.Graphics,x:number,y:number,r:number,color:number,ratio:number):void{
    g.clear(); g.fillStyle(0x050505,0.95); g.fillCircle(x,y,r+12); g.lineStyle(6,GOLD,0.85); g.strokeCircle(x,y,r+9);
    const h=r*2*Phaser.Math.Clamp(ratio,0,1); g.fillStyle(color,0.96); g.fillCircle(x,y,r); g.fillStyle(0xffffff,0.12); g.fillEllipse(x-18,y-24,42,62);
    g.fillStyle(0x030303,1); if(h<r*2) g.fillRect(x-r-2,y-r-2,r*2+4,r*2-h+2);
  }
  private redrawOrbs():void{ this.drawOrb(this.hpFill,380,983,80,0xa30912,this.hp/this.maxHp); this.drawOrb(this.manaFill,1540,983,80,0x0657a9,this.mana/this.maxMana); }
  private titleStyle(size:number):Phaser.Types.GameObjects.Text.TextStyle{return{fontFamily:"Georgia, serif",fontSize:`${size}px`,color:"#e3c47d",fontStyle:"bold",stroke:"#110b03",strokeThickness:3};}
  private bodyStyle(size:number):Phaser.Types.GameObjects.Text.TextStyle{return{fontFamily:"Georgia, serif",fontSize:`${size}px`,color:PALE,stroke:"#080503",strokeThickness:2};}
  private readonly onHealthChanged=(c:number,m:number)=>{this.hp=c;this.maxHp=m;this.redrawOrbs();};
  private readonly onManaChanged=(c:number,m:number)=>{this.mana=c;this.maxMana=m;this.redrawOrbs();};
  private readonly onBossHealthChanged=(c:number,m:number,p:number)=>{this.bossFill.width=710*Phaser.Math.Clamp(c/m,0,1);this.bossText.setText(`KRÓL SZKIELETÓW  •  FAZA ${p}`);};
  private readonly onSpellCooldownChanged=(id:SpellId,remaining:number,total:number)=>{const s=this.slots.get(id);if(!s)return;const a=remaining>0;s.shade.setVisible(a);s.timer.setVisible(a);s.shade.displayHeight=100*Phaser.Math.Clamp(remaining/total,0,1);s.timer.setText(a?(remaining/1000).toFixed(1):"");};
  private readonly onEnemyDefeated=()=>{this.kills+=1;this.killText.setText(`Szkielety: ${this.kills} / ${this.totalEnemies}`);};
  private destroy():void{this.scene.events.off(GameEvents.PlayerHealthChanged,this.onHealthChanged,this);this.scene.events.off(GameEvents.PlayerManaChanged,this.onManaChanged,this);this.scene.events.off(GameEvents.BossHealthChanged,this.onBossHealthChanged,this);this.scene.events.off(GameEvents.SpellCooldownChanged,this.onSpellCooldownChanged,this);this.scene.events.off(GameEvents.EnemyDefeated,this.onEnemyDefeated,this);}
}
